import { Injectable } from '@angular/core';
import { ActionSheetController, AlertController, ToastController } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private isAlertActive: boolean = false; // Status, ob ein Alert aktiv ist

  constructor(
    private alertController: AlertController,
    private toastController: ToastController,
    private actionSheetController: ActionSheetController
  ) {}

  // Methode für eine Ja/Nein-Bestätigung
  async presentConfirmationAlert(header: string, message: string, acceptText: string = "Bestätigen", cancelText: string = "Abbrechen"): Promise<boolean> {
    if (this.isAlertActive) {
      console.warn("Ein Alert ist bereits aktiv.");
      return Promise.resolve(false); // Falls ein Alert aktiv ist, ignorieren
    }

    this.isAlertActive = true;

    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header,
        message,
        animated: false,
        buttons: [
          {
            text: cancelText,
            role: 'cancel',
            handler: () => {
              this.isAlertActive = false;
              resolve(false);
            },
          },
          {
            text: acceptText,
            handler: () => {
              this.isAlertActive = false;
              resolve(true);
            },
          },
        ],
      });

      await alert.present();
    });
  }

  // Methode für eine Info-Bestätigung (nur OK-Button)
  async presentInfoAlert(header: string, message: string): Promise<void> {
    if (this.isAlertActive) {
      console.warn("Ein Alert ist bereits aktiv.");
      return; // Falls ein Alert aktiv ist, ignorieren
    }

    this.isAlertActive = true;

    const alert = await this.alertController.create({
      header,
      message,
      animated: false,
      buttons: [
        {
          text: 'Bestätigen',
          role: 'confirm',
          handler: () => {
            this.isAlertActive = false;
          },
        },
      ],
    });

    await alert.present();
  }

  // Methode für einen Toast
  async presentToast(message: string, duration: number = 3000, color: string = 'dark'): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration,
      color,
      position: 'top',
      animated: false,
    });

    toast.present();
  }

  async presentTextInputAlert(
    header: string,
    message: string,
    placeholder: string = "",
    acceptText: string = "Speichern",
    cancelText: string = "Abbrechen"
  ): Promise<string | null> {
    if (this.isAlertActive) {
      console.warn("Ein Alert ist bereits aktiv.");
      return Promise.resolve(null); // Falls ein Alert aktiv ist, ignorieren
    }

    this.isAlertActive = true;

    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header,
        message,
        animated: false,
        inputs: [
          {
            name: 'note',
            type: 'textarea',
            value: placeholder,
          },
        ],
        buttons: [
          {
            text: cancelText,
            role: 'cancel',
            handler: () => {
              this.isAlertActive = false; // Alert beendet
              resolve(null); // Abbrechen gibt null zurück
            },
          },
          {
            text: acceptText,
            handler: (data) => {
              this.isAlertActive = false; // Alert beendet
              resolve(data.note); // Rückgabe des eingegebenen Textes
            },
          },
        ],
      });

      await alert.present();
    });
  }

  // Methode für eine Einzelauswahl (Radio-Liste) — liefert den gewählten Wert oder null bei Abbruch
  async presentSelectAlert(
    header: string,
    message: string,
    options: { label: string; value: string }[],
    acceptText: string = "Weiter",
    cancelText: string = "Abbrechen"
  ): Promise<string | null> {
    if (this.isAlertActive) {
      console.warn("Ein Alert ist bereits aktiv.");
      return Promise.resolve(null);
    }

    this.isAlertActive = true;

    return new Promise(async (resolve) => {
      let settled = false;
      const alert = await this.alertController.create({
        header,
        message,
        animated: false,
        inputs: options.map(option => ({
          type: 'radio' as const,
          label: option.label,
          value: option.value,
        })),
        buttons: [
          {
            text: cancelText,
            role: 'cancel',
            handler: () => {
              settled = true;
              this.isAlertActive = false;
              resolve(null);
            },
          },
          {
            text: acceptText,
            handler: (value: string | undefined) => {
              settled = true;
              this.isAlertActive = false;
              resolve(value ?? null);
            },
          },
        ],
      });

      // Schließen per Backdrop/Escape ruft keinen Button-Handler auf — Sperre trotzdem lösen.
      // Nach einem Button-Klick nichts mehr anfassen: ein Folge-Dialog hält die Sperre dann bereits.
      alert.onDidDismiss().then(() => {
        if (settled) return;
        this.isAlertActive = false;
        resolve(null);
      });

      await alert.present();
    });
  }

  async presentReloginAlert(prefillEmail = ''): Promise<{ email: string; password: string } | null> {
    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header: 'Neu einloggen',
        message: 'Session abgelaufen. Bitte erneut einloggen um ungespeicherte Aufträge zu synchronisieren.',
        animated: false,
        inputs: [
          { name: 'email', type: 'email', placeholder: 'E-Mail', value: prefillEmail },
          { name: 'password', type: 'password', placeholder: 'Passwort' },
        ],
        buttons: [
          {
            text: 'Abbrechen',
            role: 'cancel',
            handler: () => resolve(null),
          },
          {
            text: 'Einloggen',
            handler: (data): boolean | void => {
              if (data.email && data.password) {
                resolve({ email: data.email, password: data.password });
              } else {
                return false;
              }
            },
          },
        ],
      });
      await alert.present();
    });
  }

  async presentActionSheet(header: string, buttons: { text: string; role?: string; icon?: string; handler?: () => void }[]): Promise<void> {
    const sheet = await this.actionSheetController.create({ header, buttons, animated: false });
    await sheet.present();
  }

}
